import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { RadioGroup, FormControlLabel, Radio, Container, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core';
import { DEVELOPMENT_STATUS, STATUS } from '../../utils/const';
import { useTranslation } from 'react-i18next';
import DevelopmentStatusChip from '../StatusButtons/DevelopmentStatusChip';
import { useMutation } from 'react-query';
import { updateObjectiveDevelopmentStatus } from '../../api/objective/objective.api';
import { addAlert } from '../../actions/alerts.action';

const useStyles = makeStyles({
  container: {
    backgroundColor: '#F4F4F4',
    padding: '20px'
  },
  radiogroup: {
    backgroundColor: '#FFFFFF',
    border: '1px solid #D6D6D6',
    boxSizing: 'border-box',
    borderRadius: '100px',
    maxWidth: '655px',
    width: '100%',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '10px 10px 20px 0px',
    padding: 0,
    position: 'relative',
    '& .MuiFormControlLabel-root': {
      marginRight: 0
    }
  },
  radio: {
    '& .MuiSvgIcon-root': {
      display: 'none'
    }
  },
  indicator: {
    maxWidth: '30%',
    width: '100%',
    height: '28px',
    position: 'absolute',
    left: 0,
    background: 'blue',
    borderRadius: '100px',
    transition: `transform 600ms cubic-bezier(.02,.94,.09,.97),
         background 300ms cubic-bezier(.17,.67,.14,1.03)`,
    transform: 'translate3d(1rem,0,0)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    color: '#FFF',
    lineHeight: '22px',
    padding: '0 8px',
    '@media screen and (max-width: 400px)': {
      maxWidth: '40%'
    },
    '@media screen and (max-width: 350px) and (min-width: 600px), (max-width: 800px)': {
      maxWidth: '45%'
    },
    '@media screen and (max-width: 300px)': {
      maxWidth: '55%'
    }
  },
  title: {
    color: '#999999',
    fontSize: '16px',
    textTransform: 'uppercase'
  }
});

const DevelopmentStatus = ({ objective, onSubmitSuccess }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [ developmentStatus, setDevelopmentStatus ] = useState(objective.development_status);
  const { IN_DANGER, NEUTRAL, AWESOME } = DEVELOPMENT_STATUS;

  const changeDevelopmentStatusMutation = useMutation(
    async developmentStatus => {
      return await updateObjectiveDevelopmentStatus({
        objective: {
          ...objective,
          developmentStatus
        }
      });
    },
    {
      onSuccess: () => {
        onSubmitSuccess();
      },
      onError: error => {
        dispatch(addAlert({ message: error.message, severity: 'error' }));
      }
    }
  );

  const handleChange = event => {
    setDevelopmentStatus(event.target.value);
    changeDevelopmentStatusMutation.mutate( event.target.value );
  };

  const addClassName = name => {
    return name === developmentStatus ? `${developmentStatus}-selected` : '';
  };

  const translatedDevelopmentStatus = type => {
    switch (type) {
      case IN_DANGER:
        return t('objective.development.status.in-danger');
      case NEUTRAL:
        return t('objective.development.status.neutral');
      case AWESOME :
        return t('objective.development.status.awesome');
      default:
        return t('objective.development.status.neutral');
    }
  };

  return (
    <Container className={ classes.container }>
      <Typography className={ classes.title }>
        { t('objective.development.status.title') }
      </Typography>
      { objective.status === STATUS.ACTIVE ?
        <RadioGroup
          aria-label="development-status"
          name="development_status"
          value={ developmentStatus }
          onChange={ handleChange }
          className={ classes.radiogroup }
        >
          <FormControlLabel
            value={ IN_DANGER }
            className={ addClassName(IN_DANGER) }
            control={ <Radio className={ classes.radio }/> }
            label={ t('objective.development.status.in-danger') }
          />
          <FormControlLabel
            value={ NEUTRAL }
            className={ addClassName( NEUTRAL) }
            control={ <Radio  className={ classes.radio }/> }
            label={ t('objective.development.status.neutral') }
          />
          <FormControlLabel
            value={ AWESOME }
            className={ addClassName(AWESOME) }
            control={ <Radio className={ classes.radio }/> }
            label={ t('objective.development.status.awesome') }
          />
          <div className={ `switch__indicator ${classes.indicator}` } >
            <p>{ translatedDevelopmentStatus(objective.development_status) }</p>
          </div>
        </RadioGroup> :
        <DevelopmentStatusChip
          developmentStatus={ objective.development_status }
          label={ t(`objective.development.status.${objective.development_status}`) }
        />
      }
    </Container>
  );
};

export default DevelopmentStatus;
